import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/data';

const catSeo: Record<string, { title: string; description: string; keywords: string }> = {
  'gaoxiao-qinzi': {
    title: '搞笑亲子漫画 - 打屁股漫画大全',
    description: '精选搞笑亲子惩罚漫画场景合集，收录最有趣的父子母女互动漫画。从妈妈生气到爸爸调皮，在欢笑声中感受亲子教育的温情与智慧。',
    keywords: '搞笑亲子漫画,亲子惩罚漫画,打屁股漫画,家庭幽默漫画,亲子教育漫画',
  },
  'xiaoyuan-chengfa': {
    title: '校园惩罚漫画 - 打屁股漫画大全',
    description: '校园惩罚漫画场景全集，收录体育课罚跑、作业忘带、男生女生搞笑斗争等经典校园漫画故事，重温青春岁月的点滴回忆。',
    keywords: '校园惩罚漫画,校园搞笑漫画,打屁股漫画,学生惩罚漫画,青春校园漫画',
  },
  'jingdian-changjing': {
    title: '经典漫画场景 - 打屁股漫画大全',
    description: '经典漫画惩罚场景回顾合集，收录龙珠、一休、火影忍者、哆啦A梦等经典作品中的惩罚训练名场面，深度分析漫画中的教育智慧。',
    keywords: '经典漫画场景,经典惩罚漫画,打屁股漫画,漫画名场面,经典动漫场景',
  },
  'youmo-jiaoyu': {
    title: '幽默教育漫画 - 打屁股漫画大全',
    description: '幽默教育漫画场景合集，寓教于乐的漫画作品赏析。抄写作业、罚站等经典惩罚场景的幽默解读，在欢笑中领悟教育真谛。',
    keywords: '幽默教育漫画,教育惩罚漫画,打屁股漫画,寓教于乐漫画,趣味教育漫画',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug) || categories[0];
  const catArticles = articles.filter((a) => a.category === category.slug);
  const seo = catSeo[category.slug] || catSeo['gaoxiao-qinzi'];
  const siteUrl = 'https://dapigumanhua.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: category.name, item: `${siteUrl}/category/${category.slug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: category.name,
        description: seo.description,
        url: `${siteUrl}/category/${category.slug}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalUrl={`${siteUrl}/category/${category.slug}`}
        ogImage={catArticles[0]?.image}
      />
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <Navbar />

      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">{category.name}</span>
          </nav>
        </div>

        {/* Page Header */}
        <section className="bg-background-100 py-12">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{category.name}</h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-2 no-scrollbar">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === category.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            {catArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章，敬请期待～</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {catArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="inline-block px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-full">
                          {category.name}
                        </span>
                        <span className="text-xs text-foreground-500">{article.date}</span>
                      </div>
                      <h3 className="font-heading font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span>{article.author}</span>
                        <span><i className="ri-eye-line mr-1"></i>{(article.readCount / 1000).toFixed(1)}k</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Other Categories */}
        <section className="py-12 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl font-bold text-foreground-900 text-center mb-6">浏览其他分类</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categories
                .filter((c) => c.slug !== category.slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer text-center"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-2">
                      <i className={`${cat.icon} text-lg`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-700">{cat.name}</span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}